import { useEffect, useState } from "react";
import { api, ApiError } from "./api.js";

export type PlatformMe = Awaited<ReturnType<typeof api.platformMe>>;
export type WorkspaceMe = Awaited<ReturnType<typeof api.workspaceMe>>;

const PLATFORM_SUPER_ADMIN = "PLATFORM_SUPER_ADMIN";

export function usePlatformPermissions() {
  const [me, setMe] = useState<PlatformMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.platformMe()
      .then((m) => { if (!cancelled) setMe(m); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const perms = new Set(me?.permissions ?? []);
  const isSuperAdmin = (me?.platformRoles ?? []).includes(PLATFORM_SUPER_ADMIN);
  return {
    me,
    loading,
    error,
    isSuperAdmin,
    has: (key: string) => isSuperAdmin || perms.has(key),
    hasAny: (...keys: string[]) => isSuperAdmin || keys.some((k) => perms.has(k))
  };
}

export function useWorkspacePermissions(workspaceId: string | undefined) {
  const [me, setMe] = useState<WorkspaceMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    let cancelled = false;
    api.workspaceMe(workspaceId)
      .then((m) => { if (!cancelled) setMe(m); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workspaceId]);
  const perms = new Set(me?.permissions ?? []);
  const isSuperAdmin = (me?.platformRoles ?? []).includes(PLATFORM_SUPER_ADMIN);
  return {
    me,
    loading,
    error,
    isSuperAdmin,
    has: (key: string) => isSuperAdmin || perms.has(key),
    hasAny: (...keys: string[]) => isSuperAdmin || keys.some((k) => perms.has(k))
  };
}
